import React, { useEffect } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { getCSRFToken } from '../utils'

function ProviderSearch (props: { zip: string, onSelect?: (providers: object[]) => void }) {
  const [search, setSearch] = React.useState<string>('')
  const [isSearching, setIsSearching] = React.useState<boolean>(false)
  const [searchResults, setSearchResults] = React.useState<[]>([])
  const [selectedProviders, setSelectedProviders] = React.useState<object[]>([])
  const [debouncedValue] = useDebounceValue<string>(search, 500)

  useEffect(() => {
    const searchProviders = async (_debouncedValue: string) => {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(`/api/providers/search?searchKey=${search}&zip=${props.zip}`, {  // eslint-disable-line thatch/no-bare-fetch
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = await response.json()
      setSearchResults(data.providers)
    }

    if (debouncedValue === '') {
      setSearchResults([])
    } else {
      searchProviders(debouncedValue)
    }
  }, [debouncedValue, props.zip, search])

  useEffect(() => {
    // Fetch the selected providers from the database
    const fetchSelectedProviders = async () => {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch('/member_onboarding/providers/all', { // eslint-disable-line thatch/no-bare-fetch
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()
      if (data.providers) {
        setSelectedProviders(data.providers)
      }
    }

    fetchSelectedProviders()
  }, [])

  const handleChange = (event: any) => {
    setIsSearching(true)
    setSearch(event.target.value)

    if (event.target.value === '') {
      setIsSearching(false)
    }
  }

  const handleSelectProvider = (provider: any) => {
    // Check if the item is already selected
    const alreadySelected = selectedProviders.some((selectedProvider: any) => selectedProvider.npi === provider.npi)

    // If this doctor has not yet been selected then add it to the selectedItems state
    // If was already selected at one point then don't do anything silently ignore
    if (!alreadySelected) {
      const updatedArray = [...selectedProviders, provider]
      setSelectedProviders(updatedArray)

      fetch('/member_onboarding/providers/', // eslint-disable-line thatch/no-bare-fetch
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken() || '',
          },
          method: 'POST',
          body: JSON.stringify({ provider }),
        }).then(() => {
        props.onSelect?.(updatedArray)
      })
    }

    setIsSearching(false)
    setSearchResults([])
  }

  const handleRemoveSelectedProvider = (providerNpi: string) => {
    const updatedArray = selectedProviders.filter((obj: any) => obj.npi !== providerNpi)
    setSelectedProviders(updatedArray)

    fetch('/member_onboarding/providers/', // eslint-disable-line thatch/no-bare-fetch
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken() || '',
        },
        method: 'DELETE',
        body: JSON.stringify({ providerNpi }),
      }).then(() => {
      props.onSelect?.(updatedArray)
    })
  }

  return (
    <div>
      <h2 className='h2 mb-4' id='search'>Search for doctors</h2>
      <div className='relative'>
        <input
          className='input-default'
          type='text'
          name='search'
          placeholder='Doctor name'
          value={isSearching ? search : ''}
          onChange={handleChange}
        />
        {isSearching
          ? (
            <div className='p-4 mt-4 absolute w-full bg-white z-50 shadow-lg border border-thatch-purple-secondary rounded-lg'>
              {searchResults.length === 0
                ? (
                  <div className='flex text-gray-400'>
                    <svg className='animate-spin -ml-1 mr-3 h-5 w-5 text-gray-400' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                    </svg>
                    <div>Loading results...</div>
                  </div>
                  )
                : (
                  <ul>
                    {searchResults.map((provider: any) => (
                      <li
                        key={provider.npi}
                        onClick={() => handleSelectProvider(provider)}
                        className='cursor-pointer hover:bg-gray-100 py-2 px-4 rounded-lg'
                      >
                        <div className='flex items-center justify-between'>
                          <span>{provider.title} {provider.first_name + ' ' + provider.last_name} <span className='text-xs'>{provider.street_address}, {provider.city}, {provider.state} <span className='text-slate-400'>{provider.specialty}</span> </span></span>
                          <div className='text-thatch-purple-secondary text-sm'>Add</div>
                        </div>
                      </li>
                    ))}
                  </ul>)}
            </div>
            )
          : (
            <div />
            )}
      </div>

      {selectedProviders.length > 0
        ? (
          <div className='pt-6 pb-2'>
            <hr />
            <h2 className='h2 pt-6 mb-4'>Your doctors</h2>
            <div>
              <div className='flex flex-col space-y-4'>
                {selectedProviders.map((selectedProvider: any) => (
                  <div key={selectedProvider.npi} className='flex items-start justify-between rounded-lg border bg-gray-50 p-4'>
                    <div className='w-full flex flex-col gap-y-2'>
                      <div><span className='font-medium'>{[selectedProvider.title, selectedProvider.first_name, selectedProvider.last_name].join(' ')}</span>, <span className='text-sm text-gray-400'>{selectedProvider.specialty}</span></div>
                      <div className='text-xs'>{selectedProvider.street_address} {selectedProvider.city}, {selectedProvider.state} {selectedProvider.zip} </div>
                      <input type='hidden' value={selectedProvider.npi} name='providers[]' />
                    </div>
                    <button onClick={() => handleRemoveSelectedProvider(selectedProvider.npi)}>
                      <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth='1.5' stroke='currentColor' aria-hidden='true' className='h-5 w-5 text-gray-500 hover:text-red-500 hover:cursor-pointer'>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )
        : (
          <div />
          )}
    </div>
  )
}

export default ProviderSearch
